// src/features/MyGymTV/UK/ReviewDS/ReviewDS.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout/Layout';
import Card from 'react-bootstrap/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Button, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import './review-ds.scss';

const ReviewDS = () => {
    const navigate = useNavigate();
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const [content, setContent] = useState([]);
    const [mediaUrls, setMediaUrls] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (selectedClubs.length === 0) {
            setLoading(false);
            return;
        }

        fetchDSContent();
    }, [selectedClubs]);

    const fetchDSContent = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const clubIds = selectedClubs.map(c => c.ClubId);

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/GetDSContent`,
                { clubIds },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            console.log('Raw response:', response.data);

            // Deduplicate the content
            const deduplicatedContent = deduplicateContent(response.data);
            setContent(deduplicatedContent);

            // Fetch media for each unique content item
            deduplicatedContent.forEach(item => {
                if (item.ContentID) {
                    fetchMediaUrl(item.ContentID);
                }
            });

        } catch (err) {
            console.error('Error fetching DS content:', err);
            setError('Failed to load content');
        } finally {
            setLoading(false);
        }
    };

    const deduplicateContent = (contentArray) => {
        const contentMap = new Map();

        contentArray.forEach(item => {
            if (contentMap.has(item.ContentID)) {
                // Content already exists, add this venue to the list
                contentMap.get(item.ContentID).venues.push({
                    VenueID: item.VenueID,
                    VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
                });
            } else {
                // First time seeing this content
                contentMap.set(item.ContentID, {
                    ...item,
                    venues: [{
                        VenueID: item.VenueID,
                        VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
                    }]
                });
            }
        });

        return Array.from(contentMap.values());
    };

    const fetchMediaUrl = async (contentID) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/DigitalSignage/${contentID}/content`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setMediaUrls(prev => ({ ...prev, [contentID]: url }));
        } catch (error) {
            console.error('Error fetching media:', error);
        }
    };

    const displayMedia = (url) => {
        if (!url) {
            return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <FontAwesomeIcon
icon={faSpinner}
size="2x"
spin
                    />
                </div>
            );
        }

        return (
            <img
                alt="DS content"
                src={url}
                style={{ maxHeight: 'auto', width: '100%' }}
            />
        );
    };

    const categorizeContent = () => {
        const today = moment().startOf('day');
        const nowPlaying = [];
        const scheduled = [];
        const expired = [];

        content.forEach(item => {
            const startDate = item.StartDate ? moment(item.StartDate) : null;
            const endDate = item.EndDate ? moment(item.EndDate) : null;

            // Now Playing
            if (
                item.IsActive &&
                (!startDate || startDate.isSameOrBefore(today)) &&
                (!endDate || endDate.isAfter(today))
            ) {
                nowPlaying.push(item);
            }
            // Scheduled
            else if (
                item.IsActive &&
                startDate && startDate.isAfter(today)
            ) {
                scheduled.push(item);
            }
            // Expired
            else if (
                !item.IsActive || (endDate && endDate.isBefore(today))
            ) {
                expired.push(item);
            }
        });

        return { nowPlaying, scheduled, expired };
    };

    const { nowPlaying, scheduled, expired } = categorizeContent();

    if (selectedClubs.length === 0) {
        return (
            <Layout>
                <div className="review-ds-container mt-5">
                    <Alert variant="warning">
                        Please select one or more venues from the menu to review content.
                    </Alert>
                </div>
            </Layout>
        );
    }

    if (loading) {
        return (
            <Layout>
                <div className="review-ds-container mt-5 text-center">
                    <FontAwesomeIcon
icon={faSpinner}
size="3x"
spin
                    />
                    <p className="mt-3">Loading content...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="review-ds-container mt-5">
                    <Alert variant="danger">{error}</Alert>
                </div>
            </Layout>
        );
    }

    if (content.length === 0) {
        return (
            <Layout>
                <div className="review-ds-container mt-5">
                    <Alert variant="info">
                        No Digital Signage content found for the selected venues.
                    </Alert>
                    <Button onClick={() => navigate('/uk/add-ds')}>
                        Add Digital Signage Content
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="review-ds-container">
                <div className="content-header mt-5 mb-4">
                    <h4>Review Digital Signage Content</h4>
                    <small className="text-muted">
                        Viewing content for {selectedClubs.length} selected venue(s)
                    </small>
                </div>

                <Card className="border-0">
                    {/* Now Playing Section */}
                    {nowPlaying.length > 0 && (
                        <div className="content-section">
                            <Card.Header className="text-end">
                                Now Playing ({nowPlaying.length})
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {nowPlaying.map((item) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={item.ContentID}
                                        >
                                            <div className="now-playing-tag d-flex justify-content-between align-items-center w-100">
                                                {item.EndDate && moment(item.EndDate).year() !== 9999 ? (
                                                    <span>
                                                        Now playing through{' '}
                                                        {moment(item.EndDate).format('MM/DD/YYYY')}
                                                    </span>
                                                ) : (
                                                    <span>Now playing with no end date</span>
                                                )}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigate(`/uk/ds-details/${item.ContentID}`)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div className="content-image-wrapper">
                                                {displayMedia(mediaUrls[item.ContentID])}
                                                {item.venues.length > 1 && (
                                                    <div className="venue-count-badge">
                                                        {item.venues.length}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                    )}

                    {/* Scheduled Section */}
                    {scheduled.length > 0 && (
                        <div className="content-section">
                            <Card.Header className="text-end">
                                Coming Soon ({scheduled.length})
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {scheduled.map((item) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={item.ContentID}
                                        >
                                            <div className="scheduled-tag d-flex justify-content-between align-items-center w-100">
                                                Starting {moment(item.StartDate).format('MM/DD/YYYY')}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigate(`/uk/ds-details/${item.ContentID}`)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div className="content-image-wrapper">
                                                {displayMedia(mediaUrls[item.ContentID])}
                                                {item.venues.length > 1 && (
                                                    <div className="venue-count-badge">
                                                        {item.venues.length}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                    )}

                    {/* Expired Section */}
                    {expired.length > 0 && (
                        <div className="content-section">
                            <Card.Header className="text-end">
                                Expired ({expired.length})
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {expired.map((item) => (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
                                            key={item.ContentID}
                                        >
                                            <div className="expired-tag d-flex justify-content-between align-items-center w-100">
                                                Expired {moment(item.EndDate).format('MM/DD/YYYY')}
                                                <FontAwesomeIcon
                                                    className="ms-2 text-white"
                                                    icon={faEdit}
                                                    onClick={() => navigate(`/uk/ds-details/${item.ContentID}`)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div className="content-image-wrapper">
                                                {displayMedia(mediaUrls[item.ContentID])}
                                                {item.venues.length > 1 && (
                                                    <div className="venue-count-badge">
                                                        {item.venues.length}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </div>
                    )}
                </Card>
            </div>
        </Layout>
    );
};

export default ReviewDS;